import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    CardActionArea,
    Grid,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Order } from '../../../services/orderService';
import { PaymentProviders } from '../CheckoutPage';
import { useTranslation } from '../../../i18n';
import api from '../../../services/api';
import usePayPalSdk from '../../../hooks/usePayPalSdk';
import PayPalButtons from '../../Marketplace/Checkout/PayPalButtons';
import MaterialSymbol from '../../common/MaterialSymbol';

interface PaymentStepProps {
    order: Order;
    paymentProviders: PaymentProviders | null;
    isCartFree: boolean;
    loading: boolean;
    error: string | null;
    onBack: () => void;
    onFreeEnrollment: () => void;
    onPaymentComplete: (order: Order) => void;
}

// Marktplatz-Rework T0: Stripe entfernt (Plan 1.4) — nur noch PayPal + kostenlos.
type PaymentMethod = 'free' | 'paypal';

const PaymentStep: React.FC<PaymentStepProps> = ({
    order,
    paymentProviders,
    isCartFree,
    loading,
    error,
    onBack,
    onFreeEnrollment,
    onPaymentComplete,
}) => {
    const { t } = useTranslation();
    const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(
        isCartFree ? 'free' : null
    );
    const [processing, setProcessing] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);
    const [paypalOrderId, setPaypalOrderId] = useState<string | null>(null);

    const availableProviders = paymentProviders?.providers.filter(p => p.enabled) || [];
    const hasPayPal = availableProviders.some(p => p.id === 'paypal');

    // Marktplatz-Rework T6: PayPal-JS-SDK statt Popup-/Polling-Pfad. Die Order
    // wird serverseitig angelegt; die Buttons liefern nur deren ID zurück und
    // melden die Genehmigung per Callback.
    const sdk = usePayPalSdk({
        clientId: paymentProviders?.paypalClientId,
        currency: paymentProviders?.currency,
        enabled: selectedMethod === 'paypal' && !isCartFree && !!paymentProviders?.paypalClientId,
    });

    const handlePayPalPayment = async () => {
        setProcessing(true);
        setPaymentError(null);

        try {
            const createResponse = await api.post<{
                success: boolean;
                paypalOrderId: string;
                paymentId: number;
            }>(`/payments/paypal/create/${order.id}`);

            if (!createResponse.data.success) {
                throw new Error('Failed to create PayPal order');
            }

            setPaypalOrderId(createResponse.data.paypalOrderId);
        } catch (err) {
            console.error('PayPal payment error:', err);
            setPaymentError(t('checkout.errors.paymentFailed'));
        }

        setProcessing(false);
    };

    const handlePayPalApproved = async (approvedOrderId: string) => {
        setProcessing(true);
        setPaymentError(null);

        try {
            const captureResponse = await api.post<{
                success: boolean;
                status: string;
                order: Order;
            }>(`/payments/paypal/capture/${order.id}`, {
                paypalOrderId: approvedOrderId,
            });

            if (captureResponse.data.success) {
                onPaymentComplete(captureResponse.data.order);
            } else {
                setPaymentError('Zahlung wurde nicht abgeschlossen');
            }
        } catch {
            setPaymentError('Fehler bei der Zahlungsverarbeitung');
        }

        setProcessing(false);
    };

    const handlePayment = () => {
        switch (selectedMethod) {
            case 'free':
                onFreeEnrollment();
                break;
            case 'paypal':
                handlePayPalPayment();
                break;
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('checkout.payment.title')}
            </Typography>

            {(error || paymentError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || paymentError}
                </Alert>
            )}

            {isCartFree ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                    Diese Bestellung ist kostenlos! Klicken Sie auf "Jetzt starten", um sofort Zugang zu erhalten.
                </Alert>
            ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Waehlen Sie Ihre bevorzugte Zahlungsmethode
                </Typography>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
                {isCartFree && paymentProviders?.allowFreeEnrollment && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                            variant={selectedMethod === 'free' ? 'outlined' : 'elevation'}
                            sx={{
                                borderColor: selectedMethod === 'free' ? 'primary.main' : undefined,
                                borderWidth: selectedMethod === 'free' ? 2 : 1,
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedMethod('free')}>
                                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                    <CheckCircleIcon
                                        sx={{ fontSize: 48, color: 'success.main', mb: 1 }}
                                    />
                                    <Typography variant="h6">
                                        Kostenlos
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Sofortiger Zugang
                                    </Typography>
                                    {selectedMethod === 'free' && (
                                        <Chip
                                            label="Ausgewaehlt"
                                            color="primary"
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}

                {!isCartFree && hasPayPal && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                            variant={selectedMethod === 'paypal' ? 'outlined' : 'elevation'}
                            sx={{
                                borderColor: selectedMethod === 'paypal' ? 'primary.main' : undefined,
                                borderWidth: selectedMethod === 'paypal' ? 2 : 1,
                            }}
                        >
                            <CardActionArea onClick={() => setSelectedMethod('paypal')}>
                                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                    <Box sx={{ color: 'var(--oa-text-2)', mb: 1, display: 'flex', justifyContent: 'center' }}>
                                        <MaterialSymbol name="payment" size={40} />
                                    </Box>
                                    <Typography variant="h6">
                                        PayPal
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Sicher und schnell bezahlen
                                    </Typography>
                                    {selectedMethod === 'paypal' && (
                                        <Chip
                                            label="Ausgewaehlt"
                                            color="primary"
                                            size="small"
                                            sx={{ mt: 1 }}
                                        />
                                    )}
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                )}
            </Grid>

            {!isCartFree && availableProviders.length === 0 && (
                <Alert severity="warning">
                    Keine Zahlungsmethoden verfuegbar. Bitte kontaktieren Sie den Support.
                </Alert>
            )}

            {paypalOrderId && (
                <Box sx={{ mb: 2 }}>
                    {sdk.error && <Alert severity="error">{t('checkout.errors.paymentFailed')}</Alert>}
                    {!sdk.ready && !sdk.error && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={28} />
                        </Box>
                    )}
                    {sdk.ready && sdk.paypal && (
                        <PayPalButtons
                            paypal={sdk.paypal}
                            paypalOrderId={paypalOrderId}
                            onApprove={handlePayPalApproved}
                            onCancel={() => setPaymentError('Zahlung wurde abgebrochen')}
                            onError={() => setPaymentError(t('checkout.errors.paymentFailed'))}
                        />
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    disabled={loading || processing}
                >
                    {t('ra.action.back')}
                </Button>
                {!paypalOrderId && (
                    <Button
                        variant="contained"
                        onClick={handlePayment}
                        disabled={!selectedMethod || loading || processing}
                        startIcon={processing ? <CircularProgress size={20} /> : undefined}
                    >
                        {isCartFree ? 'Jetzt starten' : 'Jetzt bezahlen'}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default PaymentStep;
